import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Button,
  Switch,
  Container,
  Alert,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { Link, useNavigate, useLocation } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { remainingDays, formatJpDate } from "../../../../utils/dateUtils";
import ConfirmStopModal from "../../../../shared/ConfirmStopModal";
import { LevelTooltipText, levelMap } from "../../../../shared/Constants";

const JobDetailPage = () => {
  const navigate = useNavigate();

  // ルートパラメータからIDを取得
  const { id } = useParams();

  // 求人情報のステート管理
  const [job, setJob] = useState(null);

  // テーマ情報とレスポンシブ判定
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [openConfirm, setOpenConfirm] = useState(false);
  const [switchChecked, setSwitchChecked] = useState(false);

  //　メッセージ受けとる
  const location = useLocation();
  const flashMessage = location.state?.flashMessage;

  // textareaの改行設定
  const preLineLabels = [
    "備考",
    "概要",
    "スキル詳細",
    "工程詳細",
    "ポジション詳細",
  ];

  // 雇用形態を日本語名に変換するマップ
  const EMPLOYMENT_TYPE_LABELS = {
    full_time: "正社員",
    contract_employee: "契約社員",
    contractor: "業務委託",
    temporary: "派遣社員",
    part_time: "パート・アルバイト",
    other: "その他",
  };

  // 初期表示で求人データを取得（idが存在する場合）
  useEffect(() => {
    fetch(`/company/jobs/${id}.json`)
      .then((res) => res.json())
      .then((data) => {
        setJob(data);
        setSwitchChecked(data.published_flag);
      })
      .catch((e) => {
        console.error("求人の取得中にエラーが発生しました", e);
      });
  }, [id]);

  const updatePublishedFlag = (newStatus) => {
    fetch(`/company/jobs/${job.id}/toggle_published`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          ?.content,
      },
      body: JSON.stringify({ published_flag: newStatus }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "ok") {
          setSwitchChecked(newStatus);
          setJob((prev) => ({ ...prev, published_flag: newStatus }));
        } else {
          alert("公開ステータスの更新に失敗しました。");
        }
      })
      .catch((err) => {
        console.error("公開切替エラー:", err);
        alert("通信エラーが発生しました。");
      });
  };

  const handleSwitchChange = (e) => {
    if (!e.target.checked) {
      setOpenConfirm(true);
    } else {
      updatePublishedFlag(true);
    }
  };

  const handleConfirmUnpublish = () => {
    updatePublishedFlag(false);
    setOpenConfirm(false);
  };

  // 表示する行の定義
  const rows = useMemo(() => {
    if (!job) return [];

    return [
      { label: "求人名", value: job.name || "未設定" },
      { label: "概要", value: job.description || "未設定" },
      {
        label: "年収",
        value:
          job.price_min && job.price_max
            ? `${job.price_min}〜${job.price_max}万円`
            : "未設定",
      },
      {
        label: "作業場所",
        value:
          Array.isArray(job.prefecture_names) && job.prefecture_names.length > 0
            ? job.prefecture_names.join("、")
            : job.prefecture_names || "未設定",
      },
      {
        label: "雇用形態",
        value: job.employment_type
          ? EMPLOYMENT_TYPE_LABELS[job.employment_type] || "未設定"
          : "未設定",
      },

      {
        label: "入社日",
        value: job.joining_date
          ? `${formatJpDate(job.joining_date)} 以降`
          : "未設定",
      },
      {
        label: "スキル",
        value:
          Array.isArray(job.skills) && job.skills.length > 0
            ? job.skills.map((s) => {
                const year =
                  s.experience_year != null
                    ? `${s.experience_year}年以上`
                    : "年数未設定";
                const level =
                  s.level != null
                    ? `レベル${levelMap[s.level] || s.level}以上`
                    : "レベル未設定";
                return `${s.skill_name}（${year}、${level}）`;
              })
            : ["未設定"],
      },
      { label: "スキル詳細", value: job.skill_detail || "なし" },
      {
        label: "工程",
        value:
          Array.isArray(job.operations) && job.operations.length > 0
            ? job.operations
                .sort((a, b) => a.operation_id - b.operation_id)
                .map((p) => {
                  const year =
                    p.experience_year != null
                      ? `${p.experience_year}年以上`
                      : "年数未設定";
                  return `${p.operation_name}（${year}）`;
                })
            : ["未設定"],
      },
      { label: "工程詳細", value: job.operation_detail || "なし" },
      {
        label: "ポジション",
        value:
          Array.isArray(job.roles) && job.roles.length > 0
            ? job.roles.map((p) => {
                const year =
                  p.experience_year != null
                    ? `${p.experience_year}年以上`
                    : "年数未設定";
                return `${p.role_name}（${year}）`;
              })
            : ["未設定"],
      },
      { label: "ポジション詳細", value: job.role_detail || "なし" },
      {
        label: "面談回数",
        value: job.interview_count ? `${job.interview_count}` : "未設定",
      },
      { label: "備考", value: job.remark || "なし" },
    ];
  }, [job]);

  // データ未取得時のローディング表示
  if (job === null) {
    return (
      <Box p={4} ml={4}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  return (
    <>
      {/* フラッシュメッセージ*/}
      {flashMessage && (
        <Box sx={{ px: 2, py: 1 }}>
          <Alert severity="success">
            {typeof flashMessage === "object"
              ? flashMessage.message
              : flashMessage}
          </Alert>
        </Box>
      )}

      <Container maxWidth="md" sx={{ mt: 4, mb: 10 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={4}
        >
          {/* 左：戻るリンク */}
          <Typography
            component={Link}
            to="/company/jobs"
            sx={{
              fontSize: "1.25rem",
              fontWeight: "bold",
              textDecoration: "none",
              color: "inherit",
              "&:hover": {
                opacity: 0.7,
              },
            }}
          >
            ＜ 求人一覧へ戻る
          </Typography>

          {/* 右：ボタン + 補足 */}
          <Box textAlign="right">
            <Box
              display="inline-flex"
              flexDirection="column"
              alignItems="flex-end"
            >
              <Button
                variant="outlined"
                size="small"
                onClick={() =>
                  navigate(`/company/jobs/new?duplicate_from=${job.id}`)
                }
                sx={{ fontWeight: "bold" }}
              >
                ＋ 求人を複製する
              </Button>
              <Typography
                fontSize="0.6rem"
                color="text.secondary"
                mt={0.5}
                sx={{ whiteSpace: "normal", textAlign: "right" }}
              >
                この情報を元に新規投稿を作成できます。
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* 詳細本体エリア */}
        <Paper elevation={3} sx={{ borderRadius: 2, p: 4 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            {/* 左：状態テキスト + Switch */}
            <Box display="flex" alignItems="center" gap={1}>
              <Typography fontWeight="bold" variant="h6">
                求人は{switchChecked ? "公開中" : "非公開"}
              </Typography>
              <Switch
                color="primary"
                checked={switchChecked}
                onChange={handleSwitchChange}
              />
            </Box>

            {/* 右：編集ボタン */}
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate(`/company/jobs/${job.id}/edit`)}
            >
              編集する
            </Button>
          </Box>

          {/* 掲載終了日と残日数 */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography fontSize="12px" color="gray">
              掲載期間 {formatJpDate(job.publish_end_date)}まで
            </Typography>
            <Typography fontSize="12px" color="text.secondary">
              {remainingDays(job.publish_end_date)}
            </Typography>
          </Box>

          {/* タイトル */}
          <Typography variant="h5" fontWeight="bold" mb={2}>
            {job.title}
          </Typography>

          {/* 情報テーブル */}
          <Table size="small" sx={{ mb: 4, border: "1px solid #ccc" }}>
            <TableBody>
              {rows.map((row, index) =>
                isMobile ? (
                  <React.Fragment key={index}>
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "#4b5563" : "#9ca3af",
                          color: "white",
                          py: 1,
                          fontSize: "0.8rem",
                        }}
                      >
                        {row.label}
                        {row.label === "スキル" && (
                          <Box>
                            <LevelTooltipText
                              fontSize={10}
                              color="rgba(255, 255, 255, 0.8)"
                            />
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "#f3f4f6" : "#f9fafb",
                          fontSize: "0.8rem",
                          whiteSpace: preLineLabels.includes(row.label)
                            ? "pre-line"
                            : undefined,
                          wordBreak: preLineLabels.includes(row.label)
                            ? "break-all"
                            : undefined,
                        }}
                      >
                        {Array.isArray(row.value)
                          ? row.value.map((v, i) => <div key={i}>{v}</div>)
                          : row.value}
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ) : (
                  <TableRow key={index}>
                    <TableCell
                      align="right"
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#4b5563" : "#9ca3af",
                        color: "white",
                        width: "25%",
                      }}
                    >
                      {row.label}
                      {row.label === "スキル" && (
                        <Tooltip
                          title={<LevelTooltipText />}
                          arrow
                          placement="top"
                        >
                          <InfoOutlinedIcon
                            fontSize="small"
                            sx={{
                              ml: 0.5,
                              verticalAlign: "middle",
                              color: "white",
                              opacity: 0.7,
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#f3f4f6" : "#f9fafb",
                        whiteSpace: preLineLabels.includes(row.label)
                          ? "pre-line"
                          : undefined,
                        wordBreak: preLineLabels.includes(row.label)
                          ? "break-all"
                          : undefined,
                      }}
                    >
                      {Array.isArray(row.value)
                        ? row.value.map((v, i) => <div key={i}>{v}</div>)
                        : row.value}
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>

          <Box textAlign="center" mt={4}>
            <Button
              variant="contained"
              sx={{
                // 公開中→黒、非公開→青
                backgroundColor: switchChecked ? "#111827" : "primary.main",
                color: "white",
                borderRadius: 999,
                px: 4,
                py: 1.2,
                "&:hover": {
                  backgroundColor: switchChecked ? "#1f2937" : "primary.dark",
                },
              }}
              onClick={() => {
                if (switchChecked) {
                  // 現在公開中 → モーダルで非公開確認
                  setOpenConfirm(true);
                } else {
                  // 現在非公開 → 即座に公開処理
                  updatePublishedFlag(true);
                }
              }}
            >
              {switchChecked ? "求人を非公開にする" : "求人を公開する"}
            </Button>
          </Box>
        </Paper>

        <ConfirmStopModal
          open={openConfirm}
          onClose={() => setOpenConfirm(false)}
          onConfirm={handleConfirmUnpublish}
          title="求人の公開停止"
          message={
            <>
              非公開にするとエンジニア側から
              <br />
              求人情報の閲覧が不可となります。
            </>
          }
          confirmLabel="公開を停止する"
        />
      </Container>
    </>
  );
};

export default JobDetailPage;
